/**
 * Persona Engine Health & Generation Log
 *
 * Full persona engine health dashboard with generation log filtering.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, RefreshCw } from 'lucide-react'
import { usePersonaEngineLog, usePersonaEngineHealth } from '@/hooks/usePersonaEngine'
import HealthDashboard from '@/components/persona-engine/HealthDashboard'
import type { EngineType } from '@personic/shared/types/persona-engine'
import { ENGINE_SHORT_LABELS } from '@personic/shared/types/persona-engine'

export default function PersonaEngineHealth() {
  const [limit] = useState(100)
  const [engineFilter, setEngineFilter] = useState<EngineType | undefined>()
  const [minConfidence, setMinConfidence] = useState<number | undefined>()

  const { data: health, isLoading: healthLoading } = usePersonaEngineHealth()
  const { data: logData, isLoading: logLoading, error: logError, refetch } = usePersonaEngineLog({
    limit,
    engine: engineFilter,
    min_confidence: minConfidence,
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link
            to="/tenant/engage/settings"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Engage Settings
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Persona Engine Health</h1>
          <p className="text-gray-600 mt-1">
            Monitor Persona Engine performance, generation logs, and failover rates
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Health Status */}
      {health && (
        <div className="card p-6">
          <h2 className="font-semibold text-gray-900 mb-4">System Status</h2>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Status</p>
              <p className={`font-semibold ${health.status === 'healthy' ? 'text-emerald-600' : 'text-red-600'}`}>
                {health.status}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Version</p>
              <p className="font-mono text-sm text-gray-900">{health.version}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Engines Available</p>
              <p className="font-semibold text-gray-900">{health.engines_available?.length ?? 0}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Archetypes</p>
              <p className="font-semibold text-gray-900">{health.archetype_count ?? 0}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Engine</label>
            <select
              value={engineFilter ?? ''}
              onChange={(e) => setEngineFilter(e.target.value ? e.target.value as EngineType : undefined)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">All Engines</option>
              {(['ipc', 'cat', 'rft', 'aic', 'mi', 'scarf'] as EngineType[]).map((engine) => (
                <option key={engine} value={engine}>
                  {ENGINE_SHORT_LABELS[engine]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Min Confidence</label>
            <input
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={minConfidence ?? ''}
              onChange={(e) => setMinConfidence(e.target.value ? parseFloat(e.target.value) : undefined)}
              placeholder="0.00"
              className="w-24 px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-mono"
            />
          </div>
          <div className="flex-1 text-right text-sm text-gray-500">
            {logData ? `${logData.total} total entries` : ''}
          </div>
        </div>
      </div>

      {/* Dashboard Charts */}
      <HealthDashboard
        entries={logData?.entries ?? []}
        isLoading={healthLoading || logLoading}
        error={logError}
      />

      {/* Generation Log Table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Generation Log</h2>
        </div>
        {logLoading ? (
          <div className="p-12 text-center text-gray-500">Loading generation log...</div>
        ) : !logData?.entries?.length ? (
          <div className="p-12 text-center text-gray-500">No generation log entries yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Engine</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Version</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Confidence</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Profile</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Archetype</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logData.entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3">
                      <span className="font-mono font-medium text-gray-900">
                        {ENGINE_SHORT_LABELS[entry.engine_name] || entry.engine_name.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-3 font-mono text-gray-500">
                      {entry.engine_algorithm_version}
                    </td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        entry.engine_confidence_score >= 0.7
                          ? 'bg-emerald-100 text-emerald-700'
                          : entry.engine_confidence_score >= 0.45
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {(entry.engine_confidence_score * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td className="px-6 py-3 font-mono text-gray-500">{entry.profile_id}</td>
                    <td className="px-6 py-3 text-gray-500">
                      {entry.archetype_id || '—'}
                    </td>
                    <td className="px-6 py-3 text-gray-500">
                      {new Date(entry.generation_timestamp).toLocaleString('en-US', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
